import React, { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

/**
 * Subtle "You're offline" banner that appears across the top of the app when
 * `navigator.onLine` flips to false. Slides down on disconnect, slides up on
 * reconnect. Doesn't block any UI — purely informational.
 *
 * Combined with Sprint 2 caching, the app continues to function (read recent
 * customers / quotes / certs / callouts) while offline; this banner just
 * makes the state visible so users know why submits might fail.
 *
 * Mounted once at the app root in App.js.
 */
export const OfflineIndicator = () => {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  // Used so the "Back online" toast only briefly appears, then auto-hides.
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleOffline = () => {
      setOnline(false);
      setShowReconnected(false);
    };
    const handleOnline = () => {
      setOnline(true);
      setShowReconnected(true);
      setTimeout(() => setShowReconnected(false), 3500);
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  if (online && !showReconnected) return null;

  if (!online) {
    return (
      <div
        className="fixed z-[55] bottom-4 left-1/2 -translate-x-1/2 sm:bottom-auto sm:top-4 sm:left-auto sm:right-4 sm:translate-x-0 pointer-events-none"
        role="status"
        aria-live="polite"
        data-testid="offline-indicator"
      >
        <div className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-amber-950 text-xs font-semibold pl-2.5 pr-3 py-1.5 shadow-lg shadow-amber-900/20 ring-1 ring-amber-600/40">
          <WifiOff className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span>Offline — drafts auto-saved</span>
        </div>
      </div>
    );
  }

  // online && showReconnected: brief green confirmation chip
  return (
    <div
      className="fixed z-[55] bottom-4 left-1/2 -translate-x-1/2 sm:bottom-auto sm:top-4 sm:left-auto sm:right-4 sm:translate-x-0 pointer-events-none"
      role="status"
      aria-live="polite"
      data-testid="online-indicator"
    >
      <div className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-500 text-emerald-950 text-xs font-semibold px-3 py-1.5 shadow-lg shadow-emerald-900/20 ring-1 ring-emerald-600/40">
        <span>Back online — syncing</span>
      </div>
    </div>
  );
};

export default OfflineIndicator;
